import Product from "ts/api/product";

import { unitSystems } from "constraints/general";
import {
  IExhibitorPair,
  LinkedStandProperty,
  ShowUnitSystem,
} from "@evennt-tech/api-v2-core";
import { UseTranslateResult } from "@tolgee/react";

export type LinkedPropertyResult = {
  name: LinkedStandProperty;
  unit: string;
  value: number;
};

const ProductLinkedProperties: Record<LinkedStandProperty, string> = {
  [LinkedStandProperty.None]: "None",
  [LinkedStandProperty.Area]: "Area",
  [LinkedStandProperty.WallLength]: "Wall Length",
} as const;

const LINKED_PROPERTY_KEY: Record<LinkedStandProperty, string> = {
  [LinkedStandProperty.None]: "common.product.linked_property.none",
  [LinkedStandProperty.Area]: "common.product.linked_property.area",
  [LinkedStandProperty.WallLength]:
    "common.product.linked_property.wall_length",
};

export const getLinkedPropertyLabel = (
  linkedStandProperty: LinkedStandProperty = LinkedStandProperty.None,
  t?: UseTranslateResult["t"]
) =>
  t
    ? t(LINKED_PROPERTY_KEY[linkedStandProperty])
    : ProductLinkedProperties[linkedStandProperty];

export const getLinkedPropertyValue = (
  linkedProperty?: LinkedStandProperty | null,
  exhibitorPair?: IExhibitorPair | null,
  unitSystem: ShowUnitSystem = ShowUnitSystem.METRIC
) => {
  if (!linkedProperty || !exhibitorPair) {
    return null;
  }

  if (!exhibitorPair?.stand) {
    return null;
  }
  const { standWallLength, size } = exhibitorPair.stand;

  if (!standWallLength && !size?.area) {
    return null;
  }
  const isPropertyInvalid =
    !Object.values(LinkedStandProperty).includes(linkedProperty) ||
    linkedProperty === LinkedStandProperty.None;

  if (isPropertyInvalid) {
    return null;
  }
  const result: LinkedPropertyResult = {
    name: linkedProperty,
    unit: getLinkedPropertyMetrics(unitSystem, linkedProperty),
    value: 0,
  };
  if (linkedProperty === LinkedStandProperty.WallLength) {
    return { ...result, value: Number(standWallLength || 0) };
  }
  if (linkedProperty === LinkedStandProperty.Area) {
    return { ...result, value: Number(size?.area || 0) };
  }
};

export const getLinkedPropertyMetrics = (
  unitSystem: ShowUnitSystem,
  name: LinkedStandProperty
): string => {
  const unit =
    unitSystems.find(({ value }) => value === unitSystem)?.units || "m";
  const power = name === LinkedStandProperty.Area ? "²" : "";

  return unit + power;
};

export const getLinkedPropertyByProductId = (
  products?: Product[],
  unitSystem?: ShowUnitSystem,
  exhibitorPair?: IExhibitorPair | null
) => {
  if (!products?.length) {
    return {};
  }
  return products.reduce<Record<string, LinkedPropertyResult>>(
    (acc, product) => {
      if (product.linkedStandProperty) {
        const value = getLinkedPropertyValue(
          product.linkedStandProperty,
          exhibitorPair,
          unitSystem
        );
        if (value) {
          acc[product.id] = value;
        }
      }
      return acc;
    },
    {}
  );
};

export default ProductLinkedProperties;
